import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Keyboard, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Space, Plus, Minus, Equal, Slash, HelpCircle, Search, Settings, Download, Upload, Play, Pause, Eye, BookOpen, Edit3, Save, X, RotateCcw, Undo, Redo, Copy, Home } from 'lucide-react';

interface KeyboardShortcut {
  id: string;
  key: string;
  modifiers: string[];
  description: string;
  category: 'navigation' | 'reading' | 'media' | 'accessibility' | 'editing' | 'view';
  action: () => void;
  isEnabled: boolean;
  isCustomizable: boolean;
}

interface KeyboardShortcutsProps {
  onShortcutTrigger?: (shortcut: KeyboardShortcut) => void;
  onShortcutCustomize?: (shortcut: KeyboardShortcut, newKey: string, newModifiers: string[]) => void;
  onShortcutToggle?: (shortcutId: string, enabled: boolean) => void;
}

const KeyboardShortcuts: React.FC<KeyboardShortcutsProps> = ({
  onShortcutTrigger,
  onShortcutCustomize,
  onShortcutToggle
}) => {
  const [shortcuts, setShortcuts] = useState<KeyboardShortcut[]>([]);
  const [activeShortcuts, setActiveShortcuts] = useState<Set<string>>(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const [customizing, setCustomizing] = useState<string | null>(null);
  const [newKey, setNewKey] = useState('');
  const [newModifiers, setNewModifiers] = useState<string[]>([]);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isListening, setIsListening] = useState(false);

  const keyListenerRef = useRef<((e: KeyboardEvent) => void) | null>(null);
  const helpTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    initializeShortcuts();
    setupKeyboardListeners();

    return () => {
      if (keyListenerRef.current) {
        document.removeEventListener('keydown', keyListenerRef.current);
      }
      if (helpTimeoutRef.current) {
        clearTimeout(helpTimeoutRef.current);
      }
    };
  }, []);

  const initializeShortcuts = () => {
    const defaultShortcuts: KeyboardShortcut[] = [
      // Navigation
      {
        id: 'next-article',
        key: 'ArrowRight',
        modifiers: [],
        description: 'Next article',
        category: 'navigation',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'prev-article',
        key: 'ArrowLeft',
        modifiers: [],
        description: 'Previous article',
        category: 'navigation',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'scroll-up',
        key: 'ArrowUp',
        modifiers: [],
        description: 'Scroll up',
        category: 'navigation',
        action: () => window.scrollBy(0, -100),
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'scroll-down',
        key: 'ArrowDown',
        modifiers: [],
        description: 'Scroll down',
        category: 'navigation',
        action: () => window.scrollBy(0, 100),
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'page-up',
        key: 'PageUp',
        modifiers: [],
        description: 'Page up',
        category: 'navigation',
        action: () => window.scrollBy(0, -window.innerHeight),
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'page-down',
        key: 'PageDown',
        modifiers: [],
        description: 'Page down',
        category: 'navigation',
        action: () => window.scrollBy(0, window.innerHeight),
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'home',
        key: 'Home',
        modifiers: [],
        description: 'Go to top',
        category: 'navigation',
        action: () => window.scrollTo(0, 0),
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'end',
        key: 'End',
        modifiers: [],
        description: 'Go to bottom',
        category: 'navigation',
        action: () => window.scrollTo(0, document.body.scrollHeight),
        isEnabled: true,
        isCustomizable: true
      },

      // Reading
      {
        id: 'toggle-tts',
        key: 'Space',
        modifiers: ['Ctrl'],
        description: 'Toggle text-to-speech',
        category: 'reading',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'toggle-bookmark',
        key: 'b',
        modifiers: [],
        description: 'Toggle bookmark',
        category: 'reading',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'toggle-highlight',
        key: 'h',
        modifiers: [],
        description: 'Toggle highlighting',
        category: 'reading',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'toggle-annotations',
        key: 'a',
        modifiers: [],
        description: 'Toggle annotations',
        category: 'reading',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'toggle-focus-mode',
        key: 'f',
        modifiers: [],
        description: 'Toggle focus mode',
        category: 'reading',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'toggle-reading-modes',
        key: 'r',
        modifiers: [],
        description: 'Toggle reading modes',
        category: 'reading',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },

      // Media
      {
        id: 'play-pause',
        key: 'Space',
        modifiers: [],
        description: 'Play/Pause media',
        category: 'media',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'skip-forward',
        key: 'ArrowRight',
        modifiers: ['Ctrl'],
        description: 'Skip forward 10s',
        category: 'media',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'skip-backward',
        key: 'ArrowLeft',
        modifiers: ['Ctrl'],
        description: 'Skip backward 10s',
        category: 'media',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'toggle-mute',
        key: 'm',
        modifiers: [],
        description: 'Toggle mute',
        category: 'media',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'volume-up',
        key: 'ArrowUp',
        modifiers: ['Ctrl'],
        description: 'Volume up',
        category: 'media',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'volume-down',
        key: 'ArrowDown',
        modifiers: ['Ctrl'],
        description: 'Volume down',
        category: 'media',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },

      // Accessibility
      {
        id: 'toggle-screen-reader',
        key: 's',
        modifiers: ['Ctrl', 'Alt'],
        description: 'Toggle screen reader',
        category: 'accessibility',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'toggle-high-contrast',
        key: 'c',
        modifiers: ['Ctrl', 'Alt'],
        description: 'Toggle high contrast',
        category: 'accessibility',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'increase-font-size',
        key: 'Plus',
        modifiers: ['Ctrl'],
        description: 'Increase font size',
        category: 'accessibility',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'decrease-font-size',
        key: 'Minus',
        modifiers: ['Ctrl'],
        description: 'Decrease font size',
        category: 'accessibility',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'reset-font-size',
        key: '0',
        modifiers: ['Ctrl'],
        description: 'Reset font size',
        category: 'accessibility',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },

      // Editing
      {
        id: 'copy-text',
        key: 'c',
        modifiers: ['Ctrl'],
        description: 'Copy selected text',
        category: 'editing',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'select-all',
        key: 'a',
        modifiers: ['Ctrl'],
        description: 'Select all text',
        category: 'editing',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'find-text',
        key: 'f',
        modifiers: ['Ctrl'],
        description: 'Find in text',
        category: 'editing',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'undo-action',
        key: 'z',
        modifiers: ['Ctrl'],
        description: 'Undo last action',
        category: 'editing',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'redo-action',
        key: 'y',
        modifiers: ['Ctrl'],
        description: 'Redo last action',
        category: 'editing',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },

      // View
      {
        id: 'toggle-sidebar',
        key: 's',
        modifiers: ['Ctrl'],
        description: 'Toggle sidebar',
        category: 'view',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'toggle-fullscreen',
        key: 'F11',
        modifiers: [],
        description: 'Toggle fullscreen',
        category: 'view',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'toggle-dark-mode',
        key: 'd',
        modifiers: ['Ctrl'],
        description: 'Toggle dark mode',
        category: 'view',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'zoom-in',
        key: 'Plus',
        modifiers: ['Ctrl'],
        description: 'Zoom in',
        category: 'view',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'zoom-out',
        key: 'Minus',
        modifiers: ['Ctrl'],
        description: 'Zoom out',
        category: 'view',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'reset-zoom',
        key: '0',
        modifiers: ['Ctrl'],
        description: 'Reset zoom',
        category: 'view',
        action: () => {},
        isEnabled: true,
        isCustomizable: true
      },

      // Help
      {
        id: 'show-help',
        key: '?',
        modifiers: [],
        description: 'Show keyboard shortcuts',
        category: 'view',
        action: () => setShowHelp(true),
        isEnabled: true,
        isCustomizable: true
      },
      {
        id: 'hide-help',
        key: 'Escape',
        modifiers: [],
        description: 'Hide keyboard shortcuts',
        category: 'view',
        action: () => setShowHelp(false),
        isEnabled: true,
        isCustomizable: true
      }
    ];

    setShortcuts(defaultShortcuts);
  };

  const setupKeyboardListeners = () => {
    keyListenerRef.current = (e: KeyboardEvent) => {
      const key = e.key;
      const modifiers: string[] = [];
      
      if (e.ctrlKey) modifiers.push('Ctrl');
      if (e.altKey) modifiers.push('Alt');
      if (e.shiftKey) modifiers.push('Shift');
      if (e.metaKey) modifiers.push('Cmd');

      const shortcut = shortcuts.find(s => 
        s.isEnabled && 
        s.key === key && 
        s.modifiers.length === modifiers.length &&
        s.modifiers.every(mod => modifiers.includes(mod))
      );

      if (shortcut) {
        e.preventDefault();
        shortcut.action();
        onShortcutTrigger?.(shortcut);
        
        // Show visual feedback
        setActiveShortcuts(prev => {
          const newSet = new Set(prev);
          newSet.add(shortcut.id);
          return newSet;
        });
        setTimeout(() => {
          setActiveShortcuts(prev => {
            const newSet = new Set(prev);
            newSet.delete(shortcut.id);
            return newSet;
          });
        }, 200);
      }
    };

    document.addEventListener('keydown', keyListenerRef.current);
  };

  const handleShortcutToggle = (shortcutId: string, enabled: boolean) => {
    setShortcuts(prev => 
      prev.map(s => s.id === shortcutId ? { ...s, isEnabled: enabled } : s)
    );
    onShortcutToggle?.(shortcutId, enabled);
  };

  const handleShortcutCustomize = (shortcutId: string) => {
    setCustomizing(shortcutId);
    const shortcut = shortcuts.find(s => s.id === shortcutId);
    if (shortcut) {
      setNewKey(shortcut.key);
      setNewModifiers([...shortcut.modifiers]);
    }
  };

  const handleKeyCapture = (e: React.KeyboardEvent) => {
    e.preventDefault();
    const key = e.key;
    const modifiers: string[] = [];
    
    if (e.ctrlKey) modifiers.push('Ctrl');
    if (e.altKey) modifiers.push('Alt');
    if (e.shiftKey) modifiers.push('Shift');
    if (e.metaKey) modifiers.push('Cmd');

    setNewKey(key);
    setNewModifiers(modifiers);
  };

  const saveCustomization = () => {
    if (customizing && newKey) {
      const updatedShortcuts = shortcuts.map(s => 
        s.id === customizing 
          ? { ...s, key: newKey, modifiers: newModifiers }
          : s
      );
      setShortcuts(updatedShortcuts);
      
      const shortcut = updatedShortcuts.find(s => s.id === customizing);
      if (shortcut) {
        onShortcutCustomize?.(shortcut, newKey, newModifiers);
      }
      
      setCustomizing(null);
      setNewKey('');
      setNewModifiers([]);
    }
  };

  const resetToDefault = (shortcutId: string) => {
    // Reset to default implementation would go here
    };

  const exportShortcuts = () => {
    const exportData = shortcuts.map(s => ({
      id: s.id,
      key: s.key,
      modifiers: s.modifiers,
      description: s.description,
      category: s.category,
      isEnabled: s.isEnabled
    }));
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'keyboard-shortcuts.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const importShortcuts = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target?.result as string);
          // Import logic would go here
          } catch (error) {
          console.error('Error importing shortcuts:', error);
        }
      };
      reader.readAsText(file);
    }
  };

  const filteredShortcuts = shortcuts.filter(shortcut => {
    const matchesCategory = filterCategory === 'all' || shortcut.category === filterCategory;
    const matchesSearch = shortcut.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         shortcut.key.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'navigation': return ArrowRight;
      case 'reading': return BookOpen;
      case 'media': return Play;
      case 'accessibility': return Eye;
      case 'editing': return Edit3;
      case 'view': return Settings;
      default: return Keyboard;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'navigation': return 'text-blue-600 dark:text-blue-400';
      case 'reading': return 'text-green-600 dark:text-green-400';
      case 'media': return 'text-purple-600 dark:text-purple-400';
      case 'accessibility': return 'text-orange-600 dark:text-orange-400';
      case 'editing': return 'text-red-600 dark:text-red-400';
      case 'view': return 'text-gray-600 dark:text-gray-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getCategoryBgColor = (category: string) => {
    switch (category) {
      case 'navigation': return 'bg-blue-100 dark:bg-blue-900';
      case 'reading': return 'bg-green-100 dark:bg-green-900';
      case 'media': return 'bg-purple-100 dark:bg-purple-900';
      case 'accessibility': return 'bg-orange-100 dark:bg-orange-900';
      case 'editing': return 'bg-red-100 dark:bg-red-900';
      case 'view': return 'bg-gray-100 dark:bg-gray-800';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  const formatModifiers = (modifiers: string[]) => {
    return modifiers.map(mod => {
      switch (mod) {
        case 'Ctrl': return 'Ctrl';
        case 'Alt': return 'Alt';
        case 'Shift': return 'Shift';
        case 'Cmd': return 'Cmd';
        default: return mod;
      }
    }).join(' + ');
  };

  const formatKey = (key: string) => {
    switch (key) {
      case 'ArrowUp': return '↑';
      case 'ArrowDown': return '↓';
      case 'ArrowLeft': return '←';
      case 'ArrowRight': return '→';
      case 'Space': return 'Space';
      case 'Enter': return 'Enter';
      case 'Escape': return 'Esc';
      case 'Tab': return 'Tab';
      case 'Plus': return '+';
      case 'Minus': return '-';
      case 'Equal': return '=';
      case 'Slash': return '/';
      case 'QuestionMark': return '?';
      default: return key;
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Keyboard Shortcuts
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Power user navigation and productivity shortcuts
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search shortcuts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="navigation">Navigation</option>
              <option value="reading">Reading</option>
              <option value="media">Media</option>
              <option value="accessibility">Accessibility</option>
              <option value="editing">Editing</option>
              <option value="view">View</option>
            </select>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={exportShortcuts}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
            <label className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 cursor-pointer flex items-center">
              <Upload className="h-4 w-4 mr-2" />
              Import
              <input
                type="file"
                accept=".json"
                onChange={importShortcuts}
                className="hidden"
              />
            </label>
            <button
              onClick={() => setShowHelp(!showHelp)}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center"
            >
              <HelpCircle className="h-4 w-4 mr-2" />
              Help
            </button>
          </div>
        </div>
      </div>

      {/* Shortcuts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredShortcuts.map((shortcut) => {
          const CategoryIcon = getCategoryIcon(shortcut.category);
          const isActive = activeShortcuts.has(shortcut.id);
          
          return (
            <div
              key={shortcut.id}
              className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 transition-all duration-200 ${
                isActive ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900' : 'hover:shadow-md'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${getCategoryBgColor(shortcut.category)}`}>
                  <CategoryIcon className={`h-6 w-6 ${getCategoryColor(shortcut.category)}`} />
                </div>
                
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryBgColor(shortcut.category)} ${getCategoryColor(shortcut.category)}`}>
                    {shortcut.category}
                  </span>
                  <input
                    type="checkbox"
                    checked={shortcut.isEnabled}
                    onChange={(e) => handleShortcutToggle(shortcut.id, e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {shortcut.description}
              </h3>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {shortcut.modifiers.map((mod, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded"
                    >
                      {mod}
                    </span>
                  ))}
                  <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs rounded font-medium">
                    {formatKey(shortcut.key)}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {formatModifiers(shortcut.modifiers)} + {formatKey(shortcut.key)}
                </div>
                
                <div className="flex items-center gap-2">
                  {shortcut.isCustomizable && (
                    <button
                      onClick={() => handleShortcutCustomize(shortcut.id)}
                      className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      title="Customize"
                    >
                      <Settings className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => resetToDefault(shortcut.id)}
                    className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                    title="Reset to default"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredShortcuts.length === 0 && (
        <div className="text-center py-12">
          <Keyboard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No shortcuts found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}

      {/* Customization Modal */}
      {customizing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Customize Shortcut
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Press new key combination
                </label>
                <div
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  onKeyDown={handleKeyCapture}
                  tabIndex={0}
                >
                  {formatModifiers(newModifiers)} {newModifiers.length > 0 && '+'} {formatKey(newKey)}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setCustomizing(null)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={saveCustomization}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Keyboard Shortcuts Help
              </h2>
              <button
                onClick={() => setShowHelp(false)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                Use keyboard shortcuts to navigate and control the reader more efficiently. 
                Press <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-sm">?</kbd> to show/hide this help.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {['navigation', 'reading', 'media', 'accessibility', 'editing', 'view'].map(category => {
                  const categoryShortcuts = shortcuts.filter(s => s.category === category && s.isEnabled);
                  if (categoryShortcuts.length === 0) return null;
                  
                  return (
                    <div key={category} className="space-y-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white capitalize">
                        {category} Shortcuts
                      </h3>
                      <div className="space-y-1">
                        {categoryShortcuts.map(shortcut => (
                          <div key={shortcut.id} className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">
                              {shortcut.description}
                            </span>
                            <span className="font-mono text-gray-900 dark:text-white">
                              {formatModifiers(shortcut.modifiers)} {shortcut.modifiers.length > 0 && '+'} {formatKey(shortcut.key)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyboardShortcuts;
